import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import {
  BarChart3, Scale, Sprout, AlertTriangle, TrendingUp, Store, Users, PackageCheck,
  Percent, Trophy, CalendarRange, ListChecks, Timer, CheckCircle2, Warehouse,
} from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge } from "../lib/ui";
import { fmtNum } from "../lib/constants";
import { useT } from "../lib/i18n";

const jktToday = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);
const addDays = (iso, n) => new Date(new Date(iso + "T00:00:00Z").getTime() + n * 86400000).toISOString().slice(0, 10);
const shortDate = (d) => (d || "").slice(5);
const yColor = (v, exp) => {
  if (v == null) return "text-ink";
  if (exp != null && v < exp) return "text-danger-text";
  return "text-emerald-600";
};

const SUB_TABS = [
  { key: "overview", label: "Overview", icon: Scale },
  { key: "yield", label: "Yield Analysis", icon: Percent },
  { key: "supplier", label: "Supplier Comparison", icon: Store },
  { key: "employee", label: "Employee Comparison", icon: Users },
  { key: "trend", label: "Trend", icon: TrendingUp },
];

export default function PrepareDashboard() {
  const { t } = useT();
  const today = jktToday();
  const [range, setRange] = useState({ from: addDays(today, -29), to: today, preset: "30d" });
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whList, setWhList] = useState([]);
  const [canSwitch, setCanSwitch] = useState(false);
  const [whId, setWhId] = useState(null);

  const loadWh = useCallback(async () => {
    try {
      const { data } = await api.get("/prepare/warehouses");
      setWhList(data.warehouses || []);
      setCanSwitch(!!data.can_switch);
      setWhId((cur) => cur || data.active?.id || "central");
    } catch { setWhId((cur) => cur || "central"); }
  }, []);

  const load = useCallback(async () => {
    if (!whId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/prepare/analytics", { params: { from_date: range.from, to_date: range.to, warehouse: whId } });
      setData(data);
    } finally { setLoading(false); }
  }, [range.from, range.to, whId]);

  useEffect(() => { loadWh(); }, [loadWh]);
  useEffect(() => { load(); }, [load]);

  const setPreset = (preset) => {
    const to = jktToday();
    const map = { "7d": -6, "30d": -29, "90d": -89 };
    if (preset === "month") setRange({ from: to.slice(0, 8) + "01", to, preset });
    else setRange({ from: addDays(to, map[preset]), to, preset });
  };

  const s = data?.summary;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2" data-testid="prepare-dashboard">
      <PageHeader icon={BarChart3} title={t("Prepare Dashboard")}
        subtitle={t("Yield, shrinkage & performance analytics for Meja Prepare")}>
        {canSwitch ? (
          <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2 shadow-sm" data-testid="pd-warehouse-switcher">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><Warehouse size={16} /></span>
            <select value={whId || ""} onChange={(e) => setWhId(e.target.value)} data-testid="pd-warehouse-select"
              className="cursor-pointer bg-transparent text-sm font-bold text-ink focus:outline-none">
              {whList.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
        ) : data?.warehouse ? (
          <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-3.5 py-2 shadow-sm" data-testid="pd-active-warehouse">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><Warehouse size={16} /></span>
            <p className="text-sm font-bold text-ink">{data.warehouse.name}</p>
          </div>
        ) : null}
      </PageHeader>

      {/* Date range */}
      <Card className="mb-4 p-3" data-testid="pd-range">
        <div className="flex flex-wrap items-center gap-2">
          <CalendarRange size={16} className="text-primary" />
          {[["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"], ["month", "This month"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setPreset(k)} data-testid={`pd-preset-${k}`}
              className={`rounded-full px-3 py-1 text-xs font-bold transition ${range.preset === k ? "bg-primary text-white" : "bg-app text-ink-soft hover:bg-stone-100"}`}>
              {t(lbl)}
            </button>
          ))}
          <span className="mx-1 hidden h-4 w-px bg-line sm:block" />
          <div className="flex items-center gap-1.5 text-xs text-ink-soft">
            <input type="date" value={range.from} max={range.to} data-testid="pd-from"
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value, preset: "custom" }))}
              className="rounded-lg border border-line px-2 py-1 focus:border-primary focus:outline-none" />
            <span>—</span>
            <input type="date" value={range.to} min={range.from} max={today} data-testid="pd-to"
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value, preset: "custom" }))}
              className="rounded-lg border border-line px-2 py-1 focus:border-primary focus:outline-none" />
          </div>
        </div>
      </Card>

      {/* Sub tabs */}
      <div className="mb-4 flex flex-wrap gap-2">
        {SUB_TABS.map((s2) => (
          <button key={s2.key} onClick={() => setTab(s2.key)} data-testid={`pd-tab-${s2.key}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-bold transition ${tab === s2.key ? "bg-primary text-white" : "bg-app text-ink-soft hover:bg-stone-100"}`}>
            <s2.icon size={15} /> {t(s2.label)}
          </button>
        ))}
      </div>

      {loading || !data ? <PageLoader /> : (
        <>
          {tab === "overview" && <Overview s={s} data={data} t={t} />}
          {tab === "yield" && <YieldAnalysis rows={data.yield_by_product} t={t} />}
          {tab === "supplier" && <SupplierComparison rows={data.supplier_comparison} t={t} />}
          {tab === "employee" && <EmployeeComparison rows={data.employee_comparison} t={t} />}
          {tab === "trend" && <TrendView trend={data.trend} t={t} />}
        </>
      )}
    </div>
  );
}

/* ------------------------------- Overview ------------------------------- */
function Overview({ s, data, t }) {
  const top = [...(data.yield_by_product || [])].sort((a, b) => b.total_prepared - a.total_prepared).slice(0, 5);
  const maxTop = top[0]?.total_prepared || 1;
  return (
    <div className="space-y-4" data-testid="pd-overview">
      {/* Operational KPIs first — the kitchen's day */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Kpi icon={Sprout} label={t("To Prepare")} value={fmtNum(s.to_prepare)} sub={`/ ${fmtNum(s.total_prepared_products)} ${t("Products")}`} tone="primary" testid="pd-kpi-toprepare" />
        <Kpi icon={Timer} label={t("Currently Preparing")} value={fmtNum(s.in_progress || 0)} sub={t("In progress now")} tone="warn" testid="pd-kpi-inprogress" />
        <Kpi icon={CheckCircle2} label={t("Done Today")} value={fmtNum(s.prepared_today)} sub={t("Completed today")} tone="success" testid="pd-kpi-donetoday" />
        <Kpi icon={PackageCheck} label={t("Total Processed")} value={fmtNum(s.total_records)} sub={`${fmtNum(s.total_prepared)} ${t("output")}`} tone="primary" testid="pd-kpi-records" />
      </div>

      {/* Analytics summary (secondary) */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Kpi icon={Percent} label={t("Avg Yield")} value={s.avg_yield != null ? `${s.avg_yield}%` : "—"} sub={s.overall_yield != null ? `${t("overall")} ${s.overall_yield}%` : ""} tone="success" testid="pd-kpi-yield" />
        <Kpi icon={Scale} label={t("Total Shrinkage")} value={fmtNum(s.total_shrinkage)} sub={s.avg_shrinkage_pct != null ? `${s.avg_shrinkage_pct}% ${t("avg")}` : ""} tone="warn" testid="pd-kpi-shrinkage" />
        <Kpi icon={Scale} label={t("Total Raw")} value={fmtNum(s.total_raw)} sub={`${fmtNum(s.total_prepared)} ${t("prepared")}`} tone="primary" testid="pd-kpi-raw" />
        <Kpi icon={AlertTriangle} label={t("Needs Review")} value={fmtNum(s.review_count)} sub={t("yield out of range")} tone="danger" testid="pd-kpi-review" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Top by volume */}
        <Card className="p-4 sm:p-5" data-testid="pd-top-volume">
          <SectionHead icon={Trophy} title={t("Top by Volume")} subtitle={t("Most prepared in this period")} />
          {top.length ? (
            <div className="space-y-2.5 py-1">
              {top.map((r, i) => (
                <div key={r.output_product_id} data-testid={`pd-top-${i}`}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="truncate font-semibold text-ink">{r.output_name}</span>
                    <span className="shrink-0 font-bold text-ink">{fmtNum(r.total_prepared)} {r.unit}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(6, (r.total_prepared / maxTop) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : <Empty label={t("No data for this period")} />}
        </Card>

        {/* Status + to-prepare */}
        <Card className="p-4 sm:p-5" data-testid="pd-status">
          <SectionHead icon={ListChecks} title={t("Workload & Status")} subtitle={t("Preparation pipeline this period")} />
          <div className="grid grid-cols-2 gap-3">
            <Stat label={t("To Prepare")} value={`${fmtNum(s.to_prepare)} / ${fmtNum(s.total_prepared_products)}`} />
            <Stat label={t("Done Today")} value={fmtNum(s.prepared_today)} />
            <Stat label={t("Currently Preparing")} value={fmtNum(s.in_progress || 0)} />
            <Stat label={t("Completed")} value={fmtNum(s.status_breakdown?.COMPLETED || 0)} />
            <Stat label={t("Total Raw")} value={fmtNum(s.total_raw)} />
            <Stat label={t("Total Prepared")} value={fmtNum(s.total_prepared)} />
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ----------------------------- Yield Analysis ----------------------------- */
function YieldAnalysis({ rows, t }) {
  if (!rows?.length) return <Card className="p-10"><Empty label={t("No data for this period")} /></Card>;
  return (
    <Card className="overflow-hidden p-0" data-testid="pd-yield-table">
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
          <th className="px-4 py-3">{t("Product")}</th>
          <th className="px-4 py-3 text-right">{t("Records")}</th>
          <th className="px-4 py-3 text-right">{t("Prepared")}</th>
          <th className="px-4 py-3 text-right">{t("Avg Yield")}</th>
          <th className="hidden px-4 py-3 text-right sm:table-cell">{t("Expected")}</th>
          <th className="px-4 py-3 text-right">{t("Variance")}</th>
          <th className="hidden px-4 py-3 text-right md:table-cell">{t("Shrinkage")}</th>
          <th className="px-4 py-3 text-center">{t("Review")}</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.output_product_id} className="border-b border-line/60 hover:bg-stone-50/60" data-testid={`pd-yield-row-${r.output_product_id}`}>
              <td className="px-4 py-3"><span className="flex items-center gap-2 font-semibold text-ink"><Sprout size={14} className="text-emerald-600" />{r.output_name}</span></td>
              <td className="px-4 py-3 text-right text-ink-soft">{fmtNum(r.count)}</td>
              <td className="px-4 py-3 text-right font-semibold">{fmtNum(r.total_prepared)} {r.unit}</td>
              <td className={`px-4 py-3 text-right font-bold ${yColor(r.avg_yield, r.expected_yield)}`}>{r.avg_yield != null ? `${r.avg_yield}%` : "—"}</td>
              <td className="hidden px-4 py-3 text-right text-ink-soft sm:table-cell">{r.expected_yield != null ? `${r.expected_yield}%` : "—"}</td>
              <td className="px-4 py-3 text-right">{r.yield_variance != null ? <span className={`font-bold ${r.yield_variance < 0 ? "text-danger-text" : "text-emerald-600"}`}>{r.yield_variance > 0 ? "+" : ""}{r.yield_variance}%</span> : "—"}</td>
              <td className="hidden px-4 py-3 text-right text-ink-soft md:table-cell">{fmtNum(r.total_shrinkage)} {r.unit}</td>
              <td className="px-4 py-3 text-center">{r.review_count > 0 ? <Badge tone="danger"><AlertTriangle size={11} /> {fmtNum(r.review_count)}</Badge> : <span className="text-ink-mute">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </Card>
  );
}

/* --------------------------- Supplier Comparison --------------------------- */
function SupplierComparison({ rows, t }) {
  if (!rows?.length) return <Card className="p-10"><Empty label={t("No data for this period")} /></Card>;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="pd-supplier">
      <Card className="p-4 sm:p-5">
        <SectionHead icon={Trophy} title={t("Yield by Supplier")} subtitle={t("Higher yield = better raw material quality")} />
        <YieldBars rows={rows.map((r) => ({ name: r.supplier_name, yield: r.avg_yield }))} />
      </Card>
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
            <th className="px-4 py-3">{t("Supplier")}</th>
            <th className="px-4 py-3 text-right">{t("Records")}</th>
            <th className="px-4 py-3 text-right">{t("Avg Yield")}</th>
            <th className="hidden px-4 py-3 text-right sm:table-cell">{t("Shrinkage")}</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-line/60 hover:bg-stone-50/60" data-testid={`pd-supplier-row-${i}`}>
                <td className="px-4 py-3 font-semibold text-ink">{r.supplier_name}<span className="block text-[11px] font-normal text-ink-mute">{fmtNum(r.products)} {t("Products")}</span></td>
                <td className="px-4 py-3 text-right text-ink-soft">{fmtNum(r.count)}</td>
                <td className="px-4 py-3 text-right"><span className="font-bold text-ink">{r.avg_yield != null ? `${r.avg_yield}%` : "—"}</span></td>
                <td className="hidden px-4 py-3 text-right text-ink-soft sm:table-cell">{fmtNum(r.total_shrinkage)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>
    </div>
  );
}

/* --------------------------- Employee Comparison --------------------------- */
function EmployeeComparison({ rows, t }) {
  if (!rows?.length) return <Card className="p-10"><Empty label={t("No data for this period")} /></Card>;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="pd-employee">
      <Card className="p-4 sm:p-5">
        <SectionHead icon={Users} title={t("Yield by Employee")} subtitle={t("Consistency of preparation per staff")} />
        <YieldBars rows={rows.map((r) => ({ name: r.employee_name, yield: r.avg_yield }))} />
      </Card>
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
            <th className="px-4 py-3">{t("Employee")}</th>
            <th className="px-4 py-3 text-right">{t("Records")}</th>
            <th className="px-4 py-3 text-right">{t("Avg Yield")}</th>
            <th className="px-4 py-3 text-center">{t("Review")}</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.employee_id} className="border-b border-line/60 hover:bg-stone-50/60" data-testid={`pd-employee-row-${i}`}>
                <td className="px-4 py-3 font-semibold text-ink">{r.employee_name}<span className="block text-[11px] font-normal text-ink-mute">{fmtNum(r.total_prepared)} {t("prepared")}</span></td>
                <td className="px-4 py-3 text-right text-ink-soft">{fmtNum(r.count)}</td>
                <td className="px-4 py-3 text-right"><span className="font-bold text-ink">{r.avg_yield != null ? `${r.avg_yield}%` : "—"}</span></td>
                <td className="px-4 py-3 text-center">{r.review_count > 0 ? <Badge tone="danger">{fmtNum(r.review_count)}</Badge> : <span className="text-ink-mute">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>
    </div>
  );
}

/* -------------------------------- Trend -------------------------------- */
function TrendView({ trend, t }) {
  const hasYield = trend.some((p) => p.avg_yield != null);
  const hasVol = trend.some((p) => p.total_prepared > 0);
  return (
    <div className="grid grid-cols-1 gap-4" data-testid="pd-trend">
      <Card className="p-4 sm:p-5">
        <SectionHead icon={Percent} title={t("Yield Trend")} subtitle={t("Average yield % over time")} />
        {hasYield ? (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={trend} margin={{ top: 5, right: 8, left: 4, bottom: 0 }}>
              <defs><linearGradient id="pdYield" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#16a34a" stopOpacity={0.35} /><stop offset="100%" stopColor="#16a34a" stopOpacity={0.02} />
              </linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1ece6" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: "#9b8e82" }} interval="preserveStartEnd" minTickGap={24} />
              <YAxis tick={{ fontSize: 10, fill: "#9b8e82" }} width={36} unit="%" domain={[0, 100]} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Area type="monotone" dataKey="avg_yield" stroke="#16a34a" strokeWidth={2.5} fill="url(#pdYield)" connectNulls />
            </AreaChart>
          </ResponsiveContainer>
        ) : <Empty label={t("No data for this period")} />}
      </Card>

      <Card className="p-4 sm:p-5">
        <SectionHead icon={TrendingUp} title={t("Prepared Quantity Trend")} subtitle={t("Prepared output per day")} />
        {hasVol ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trend} margin={{ top: 5, right: 8, left: 4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1ece6" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: "#9b8e82" }} interval="preserveStartEnd" minTickGap={24} />
              <YAxis tick={{ fontSize: 10, fill: "#9b8e82" }} width={36} />
              <Tooltip formatter={(v) => fmtNum(v)} />
              <Bar dataKey="total_prepared" fill="#ea580c" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty label={t("No data for this period")} />}
      </Card>
    </div>
  );
}

/* ------------------------------- Shared UI ------------------------------- */
function YieldBars({ rows }) {
  const data = rows.filter((r) => r.yield != null).slice(0, 8);
  if (!data.length) return <Empty label="—" />;
  return (
    <ResponsiveContainer width="100%" height={Math.max(160, data.length * 38)}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1ece6" horizontal={false} />
        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "#9b8e82" }} unit="%" />
        <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#5b5048" }} />
        <Tooltip formatter={(v) => `${v}%`} />
        <Bar dataKey="yield" radius={[0, 4, 4, 0]}>
          {data.map((r, i) => <Cell key={i} fill={r.yield >= 70 ? "#16a34a" : r.yield >= 50 ? "#ea580c" : "#dc2626"} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function SectionHead({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <Icon size={16} className="text-primary" />
      <div><h3 className="font-heading text-sm font-extrabold text-ink">{title}</h3>
        <p className="text-[11px] text-ink-mute">{subtitle}</p></div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone = "primary", testid }) {
  const tones = { primary: "bg-primary-light text-primary", success: "bg-emerald-50 text-emerald-600", warn: "bg-amber-50 text-amber-600", danger: "bg-danger-light text-danger-text" };
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}><Icon size={18} /></span>
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold uppercase text-ink-mute">{label}</p>
          <p className="truncate text-lg font-bold text-ink">{value}</p>
          {sub && <p className="truncate text-[11px] text-ink-mute">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-line p-3">
      <p className="text-[10px] font-semibold uppercase text-ink-mute">{label}</p>
      <p className="font-bold text-ink">{value}</p>
    </div>
  );
}

const Empty = ({ label }) => <div className="flex h-[160px] items-center justify-center text-sm text-ink-mute">{label}</div>;
